import I18n from 'I18n';
import { Fragment } from 'react';
import PortalIdParser from 'PortalIdParser';
import SkeletonText from 'flow-doctor-lib/components/common/SkeletonText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import WalmartFilterBranchOperatorLabel from 'flow-doctor-lib/components/filters/WalmartFilterBranchOperatorLabel';
import useILSList from 'flow-doctor-lib/hooks/useILSList';
import useFormSubmission from 'flow-doctor-lib/hooks/useFormSubmission';
import useContentPage from 'flow-doctor-lib/hooks/useContentPage';
import useCTAByGuid from 'flow-doctor-lib/hooks/useCTAsByGuids';
import useEmailByCampaignId from 'flow-doctor-lib/hooks/useEmailByCampaignId';
import useEmailSubscription from 'flow-doctor-lib/hooks/useEmailSubscription';
import useNameForInListType from 'flow-doctor-lib/hooks/useNameForInListType';
import useSurveyMonkeySurvey from 'flow-doctor-lib/hooks/useSurveyMonkeySurvey';
import useLegacyCBE from 'flow-doctor-lib/hooks/useLegacyCBE';
import useLegacyIntegrationEventProperties from 'flow-doctor-lib/hooks/useLegacyIntegrationEventProperties';
import useAdsFilterProperties from 'flow-doctor-lib/hooks/useAdsFilterProperties';
import useAdsFilterValues, { getSearchTermValuesForAdFilter } from 'flow-doctor-lib/hooks/useAdsFilterValues';
import { RangedTimeDisplayLabel, TimePointDisplayLabel } from 'flow-doctor-lib/utils/timePointRenderUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ENUM_PERSISTENCE_INCLUSION_OPERATORS = ['HAS_EVER_CONTAINED_ALL', 'HAS_NEVER_CONTAINED_ALL', 'HAS_NEVER_BEEN_ANY_OF', 'HAS_EVER_BEEN_EXACTLY', 'HAS_NEVER_BEEN_EXACTLY', 'DOES_NOT_CONTAIN_ALL'];
const FilterLineWithPruningRefineBy = ({
  pruningRefineBy,
  eventDescription
}) => {
  switch (pruningRefineBy.type) {
    case 'AbsoluteComparativeTimestampRefineBy':
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: pruningRefineBy.comparison === 'BEFORE' ? 'flow-doctor-lib.filters.pruningRefineBy.onOrBefore_jsx' : 'flow-doctor-lib.filters.pruningRefineBy.onOrAfter_jsx',
        elements: {
          b: 'b'
        },
        options: {
          eventDescription,
          timestamp: I18n.moment(pruningRefineBy.timestamp).format('L')
        }
      });
    case 'AbsoluteRangedTimestampRefineBy':
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: pruningRefineBy.rangeType === 'BETWEEN' ? 'flow-doctor-lib.filters.pruningRefineBy.between_jsx' : 'flow-doctor-lib.filters.pruningRefineBy.notBetween_jsx',
        elements: {
          b: 'b'
        },
        options: {
          eventDescription,
          lowerTimestamp: I18n.moment(pruningRefineBy.lowerTimestamp).format('L'),
          upperTimestamp: I18n.moment(pruningRefineBy.upperTimestamp).format('L')
        }
      });
    case 'RelativeRangedTimestampRefineBy':
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: pruningRefineBy.rangeType === 'BETWEEN' ? 'flow-doctor-lib.filters.pruningRefineBy.betweenAgo_jsx' : 'flow-doctor-lib.filters.pruningRefineBy.notBetweenAgo_jsx',
        elements: {
          b: 'b'
        },
        options: {
          eventDescription,
          timeUnit: pruningRefineBy.lowerBoundOffset.timeUnit.toLowerCase(),
          timeAmount: pruningRefineBy.lowerBoundOffset.amount
        }
      });
    case 'RelativeComparativeTimestampRefineBy':
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: pruningRefineBy.comparison === 'BEFORE' ? 'flow-doctor-lib.filters.pruningRefineBy.before_jsx' : 'flow-doctor-lib.filters.pruningRefineBy.after_jsx',
        elements: {
          b: 'b'
        },
        options: {
          eventDescription,
          timeUnit: pruningRefineBy.timeOffset.timeUnit.toLowerCase(),
          timeAmount: pruningRefineBy.timeOffset.amount
        }
      });
    default:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: eventDescription
      });
  }
};
const FilterLineWithCoalescingRefineBy = ({
  coalescingRefineBy,
  eventDescription
}) => {
  if (coalescingRefineBy.type === 'NumOccurrencesRefineBy') {
    const {
      maxOccurrences,
      minOccurrences
    } = coalescingRefineBy;
    if (maxOccurrences && minOccurrences) {
      if (maxOccurrences === minOccurrences) {
        return /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "flow-doctor-lib.filters.coalescingRefineBy.exactly_jsx",
          elements: {
            b: 'b'
          },
          options: {
            eventDescription,
            occurrences: maxOccurrences
          }
        });
      }
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "flow-doctor-lib.filters.coalescingRefineBy.between_jsx",
        elements: {
          b: 'b'
        },
        options: {
          eventDescription,
          minOccurrences,
          maxOccurrences
        }
      });
    }
    if (maxOccurrences != null) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "flow-doctor-lib.filters.coalescingRefineBy.atMost_jsx",
        elements: {
          b: 'b'
        },
        options: {
          eventDescription,
          maxOccurrences
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "flow-doctor-lib.filters.coalescingRefineBy.atLeast_jsx",
      elements: {
        b: 'b'
      },
      options: {
        eventDescription,
        minOccurrences
      }
    });
  }
  switch (coalescingRefineBy.setType) {
    case 'ALL_INCLUDE_EMPTY':
    case 'ANY_INCLUDE_EMPTY':
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "flow-doctor-lib.filters.coalescingRefineBy.includeEmpty_jsx",
        elements: {
          i: 'i'
        },
        options: {
          eventDescription
        }
      });
    case 'NONE_EXCLUDE_EMPTY':
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "flow-doctor-lib.filters.coalescingRefineBy.excludeEmpty_jsx",
        elements: {
          i: 'i'
        },
        options: {
          eventDescription
        }
      });
    default:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: eventDescription
      });
  }
};
export const FilterLineForPropertyFilter = ({
  filter,
  propertyLabel,
  propertyValueLabels
}) => {
  const {
    operation,
    property
  } = filter;
  const propertyName = propertyLabel || property;
  switch (operation.propertyType) {
    case 'alltypes':
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `flow-doctor-lib.filters.property.operators.${operation.operator}`,
        options: {
          propertyName
        }
      });
    case 'bool':
    case 'number':
    case 'string':
      {
        const stringOperationValue = operation.value.toString();
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: operation.includeObjectsWithNoValueSet || operation.defaultValue != null ? `flow-doctor-lib.filters.property.operators.${operation.operator}_WITH_EMPTY` : `flow-doctor-lib.filters.property.operators.${operation.operator}`,
          options: {
            propertyName,
            propertyValue: propertyValueLabels ? propertyValueLabels[stringOperationValue] || stringOperationValue : stringOperationValue
          }
        });
      }
    // Deprecated with release of timepoint filters
    case 'datetime':
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: operation.includeObjectsWithNoValueSet || operation.defaultValue != null ? `flow-doctor-lib.filters.property.operators.${operation.operator}_WITH_EMPTY` : `flow-doctor-lib.filters.property.operators.${operation.operator}`,
        options: {
          propertyName,
          propertyValue: I18n.moment(operation.timestamp).format('L')
        }
      });
    case 'number-ranged':
      {
        const {
          lowerBound,
          upperBound
        } = operation;
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: operation.includeObjectsWithNoValueSet || operation.defaultValue != null ? `flow-doctor-lib.filters.property.operators.${operation.operator}_WITH_EMPTY` : `flow-doctor-lib.filters.property.operators.${operation.operator}`,
          options: {
            propertyName,
            firstValue: lowerBound.toString(),
            secondValue: upperBound.toString()
          }
        });
      }
    // Deprecated with release of timepoint filters
    case 'datetime-comparative':
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: operation.includeObjectsWithNoValueSet || operation.defaultValue != null ? `flow-doctor-lib.filters.property.operators.${operation.operator}_WITH_EMPTY` : `flow-doctor-lib.filters.property.operators.${operation.operator}`,
        options: {
          propertyName,
          propertyValue: operation.comparisonPropertyName
        }
      });
    // Deprecated with release of timepoint filters
    case 'property-updated-comparative':
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `flow-doctor-lib.filters.property.operators.${operation.operator}_PROPERTY`,
        options: {
          propertyName,
          propertyValue: operation.comparisonPropertyName
        }
      });
    // Deprecated with release of timepoint filters
    case 'datetime-rolling':
    case 'rolling-property-updated':
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: operation.includeObjectsWithNoValueSet || operation.defaultValue != null ? `flow-doctor-lib.filters.property.operators.${operation.operator}_WITH_EMPTY` : `flow-doctor-lib.filters.property.operators.${operation.operator}`,
        options: {
          propertyName,
          propertyValue: operation.numberOfDays
        }
      });
    case 'enumeration':
      {
        const labeledOptions = propertyValueLabels ? operation.values.map(option => propertyValueLabels[option]) : operation.values;
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: operation.includeObjectsWithNoValueSet || operation.defaultValue != null ? `flow-doctor-lib.filters.property.operators.${operation.operator}_WITH_EMPTY` : `flow-doctor-lib.filters.property.operators.${operation.operator}`,
          options: {
            propertyName,
            propertyValue: I18n.formatList(labeledOptions, {
              exclusive: !ENUM_PERSISTENCE_INCLUSION_OPERATORS.includes(filter.operation.operator)
            })
          }
        });
      }
    case 'multistring':
      {
        const multistringOperation = filter.operation;
        const pruningRefineBy = multistringOperation.pruningRefineBy;
        let operator = operation.operator;
        if ((pruningRefineBy === null || pruningRefineBy === void 0 ? void 0 : pruningRefineBy.type) === 'AllHistoryRefineBy') {
          if (operator === 'IS_EQUAL_TO') {
            operator = 'HAS_EVER_BEEN_EQUAL_TO';
          } else if (operator === 'IS_NOT_EQUAL_TO') {
            operator = 'HAS_NEVER_BEEN_EQUAL_TO';
          }
        }
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: operation.includeObjectsWithNoValueSet || operation.defaultValue != null ? `flow-doctor-lib.filters.property.operators.${operator}_ANY_OF_WITH_EMPTY` : `flow-doctor-lib.filters.property.operators.${operator}_ANY_OF`,
          options: {
            propertyName,
            propertyValue: I18n.formatList(operation.values, {
              exclusive: !ENUM_PERSISTENCE_INCLUSION_OPERATORS.includes(filter.operation.operator)
            })
          }
        });
      }
    case 'timepoint':
      {
        return /*#__PURE__*/_jsx(TimePointDisplayLabel, {
          propertyName: propertyName,
          operation: operation
        });
      }
    case 'rangedtime':
      {
        return /*#__PURE__*/_jsx(RangedTimeDisplayLabel, {
          propertyName: propertyName,
          operation: operation
        });
      }
    default:
      throw new Error(`Unexpected property filter type found on operation ${JSON.stringify(operation)}. Please add readable copy for this case`);
  }
};
export const FilterLineForEmailSubscriptionFilter = ({
  filter
}) => {
  const {
    data,
    loading,
    error
  } = useEmailSubscription({
    portalId: PortalIdParser.get(),
    subscriptionIds: filter.subscriptionIds
  });
  if (!error && (loading || !data)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }

  // Note: There should only ever be one if it's coming from an enrollment filter
  const subscriptionType = filter.acceptedStatuses[0];
  return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.emailSubscription.${subscriptionType}`,
    options: {
      subscriptions: data ? data.map(sub => sub.name) : filter.subscriptionIds
    }
  });
};
const FilterLineForInListTypeFilter = ({
  filter
}) => {
  const {
    data: inListName,
    loading: inListTypeLoading,
    error: inListTypeError
  } = useNameForInListType({
    portalId: PortalIdParser.get(),
    metadata: filter.metadata
  });
  if (!inListTypeError && (inListTypeLoading || !inListName)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.inList.inListType.${filter.metadata.inListType}.${filter.operator}`,
    options: {
      listId: inListName || filter.listId
    }
  });
};
export const FilterLineForInListFilter = ({
  filter
}) => {
  const {
    data: listData,
    loading: listDataLoading,
    error: listError
  } = useILSList({
    portalId: PortalIdParser.get(),
    listId: filter.listId
  }, {
    skip: !!filter.metadata
  });
  if (filter.metadata) {
    return /*#__PURE__*/_jsx(FilterLineForInListTypeFilter, {
      filter: filter
    });
  }
  if (!listError && (listDataLoading || !listData)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.inList.${filter.operator}`,
    options: {
      listId: listData ? listData.name : filter.listId
    }
  });
};
export const FilterLineForAdsSearchFilter = ({
  filter
}) => {
  const {
    data: propertyData,
    loading: propertyLoading,
    error: propertyError
  } = useAdsFilterProperties({
    portalId: PortalIdParser.get(),
    filter
  });
  const shouldFetchFilterValues = Boolean(filter.searchTermType === 'ID' && propertyData);
  const {
    data: filterValuesData,
    loading: filterValuesLoading,
    error: filterValuesError
  } = useAdsFilterValues({
    portalId: PortalIdParser.get(),
    adNetwork: filter.adNetwork,
    entityType: filter.entityType,
    ids: filter.searchTerms
  }, {
    skip: !shouldFetchFilterValues
  });
  if (!propertyError && (propertyLoading || !propertyData) || shouldFetchFilterValues && !filterValuesError && (filterValuesLoading || !filterValuesData)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  const searchProperty = propertyData ? propertyData.label : `${filter.adNetwork.toLowerCase()} ${filter.entityType.toLowerCase()} ${filter.searchTermType.toLowerCase()}`;
  const searchValues = propertyData ? getSearchTermValuesForAdFilter(filter, propertyData, filterValuesData) : filter.searchTerms;
  return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.adsSearch.${filter.operator}`,
    options: {
      searchProperty,
      searchValues
    }
  });
};
export const FilterLineForFormSubmissionFilter = ({
  filter
}) => {
  const formId = filter.formId;
  const pageId = filter.pageId;
  const {
    data: formData,
    loading: formLoading,
    error: formError
  } = useFormSubmission({
    portalId: PortalIdParser.get(),
    formId: filter.formId
  }, {
    skip: !formId
  });
  const {
    data: pageData,
    loading: pageLoading,
    error: pageError
  } = useContentPage({
    portalId: PortalIdParser.get(),
    pageId
  }, {
    skip: !pageId
  });
  if (!(formError || pageError) && (formLoading || pageLoading || formId && !formData || pageId && !pageData)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  const displayFormId = filter.formId ? formData ? formData.name : filter.formId : I18n.text('flow-doctor-lib.filters.formSubmission.ANY_FORM');
  const displayPageId = pageData ? pageData.label : pageId;
  let eventDescription = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.formSubmission.${filter.filterType}`,
    options: {
      formId: displayFormId,
      pageId: displayPageId
    }
  });
  if (filter.coalescingRefineBy) {
    // Special logic because form submissions are the only filters that currently change text based on the set type differing here
    if (filter.coalescingRefineBy.type === 'SetOccurrencesRefineBy' && filter.coalescingRefineBy.setType === 'NONE') {
      eventDescription = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `flow-doctor-lib.filters.formSubmission.${filter.filterType}_NOT`,
        options: {
          formId: displayFormId,
          pageId: displayPageId
        }
      });
    }
    eventDescription = /*#__PURE__*/_jsx(FilterLineWithCoalescingRefineBy, {
      coalescingRefineBy: filter.coalescingRefineBy,
      eventDescription: eventDescription
    });
  }
  if (filter.pruningRefineBy) {
    return /*#__PURE__*/_jsx(FilterLineWithPruningRefineBy, {
      pruningRefineBy: filter.pruningRefineBy,
      eventDescription: eventDescription
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: eventDescription
  });
};
export const FilterLineForPageViewFilter = ({
  filter
}) => {
  const eventDescription = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.pageView.${filter.operator}`,
    options: {
      pageUrl: filter.pageUrl
    }
  });
  if (filter.pruningRefineBy) {
    return /*#__PURE__*/_jsx(FilterLineWithPruningRefineBy, {
      pruningRefineBy: filter.pruningRefineBy,
      eventDescription: eventDescription
    });
  }
  if (filter.coalescingRefineBy) {
    return /*#__PURE__*/_jsx(FilterLineWithCoalescingRefineBy, {
      coalescingRefineBy: filter.coalescingRefineBy,
      eventDescription: eventDescription
    });
  }
  return eventDescription;
};
const getFilterLineFromEmailEventFilterLines = (filter, emailName) => {
  if (filter.emailFilterLines.length === 1) {
    const line = filter.emailFilterLines[0];
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `flow-doctor-lib.filters.emailEvent.${line.eventType}`,
      options: {
        emailId: emailName
      }
    });
  }

  // Must have two events now
  const [firstLine, secondLine] = filter.emailFilterLines;
  let eventTypeKey = '';
  if (firstLine.eventType === 'OPEN' && secondLine.eventType === 'CLICK') {
    eventTypeKey = 'OPEN_NO_CLICK';
  } else if (firstLine.eventType === 'OPEN' && secondLine.eventType === 'REPLIED') {
    eventTypeKey = 'OPEN_NO_REPLY';
  } else if (firstLine.eventType === 'DELIVERED' && secondLine.eventType === 'OPEN') {
    eventTypeKey = 'DELIVERED_NO_OPEN';
  } else if (firstLine.eventType === 'SENT' && secondLine.eventType === 'CLICK') {
    eventTypeKey = 'SENT_NO_CLICK';
  } else if (firstLine.eventType === 'SENT' && secondLine.eventType === 'DELIVERED') {
    eventTypeKey = 'SENT_NO_DELIVERED';
  }
  return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.emailEvent.${eventTypeKey}`,
    options: {
      emailId: emailName
    }
  });
};
export const FilterLineForEmailEventFilter = ({
  filter
}) => {
  const {
    data,
    loading,
    error
  } = useEmailByCampaignId({
    portalId: PortalIdParser.get(),
    emailCampaignId: filter.emailId
  });
  if (!error && (loading || !data)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  const eventDescription = getFilterLineFromEmailEventFilterLines(filter, data ? data.name : filter.emailId.toString());
  const lineWithPruning = filter.emailFilterLines.find(line => !!line.pruningRefineBy);
  if (lineWithPruning) {
    return /*#__PURE__*/_jsx(FilterLineWithPruningRefineBy, {
      pruningRefineBy: lineWithPruning.pruningRefineBy,
      eventDescription: eventDescription
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: eventDescription
  });
};
const CTA_PLACEMENT_OPERATORS = ['HAS_CLICKED_CTA_PLACEMENT', 'HAS_NOT_CLICKED_CTA_PLACEMENT', 'HAS_OPENED_CTA_PLACEMENT', 'HAS_NOT_OPENED_CTA_PLACEMENT'];
export const FilterLineForCtaFilter = ({
  filter
}) => {
  const {
    data,
    loading,
    error
  } = useCTAByGuid({
    portalId: PortalIdParser.get(),
    guid: filter.ctaName,
    isPlacement: CTA_PLACEMENT_OPERATORS.includes(filter.operator)
  });
  if (!error && (loading || !data)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  const eventDescription = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.cta.${filter.operator}`,
    options: {
      ctaName: data ? data.name : filter.ctaName
    }
  });
  if (filter.pruningRefineBy) {
    return /*#__PURE__*/_jsx(FilterLineWithPruningRefineBy, {
      pruningRefineBy: filter.pruningRefineBy,
      eventDescription: eventDescription
    });
  }
  if (filter.coalescingRefineBy) {
    return /*#__PURE__*/_jsx(FilterLineWithCoalescingRefineBy, {
      coalescingRefineBy: filter.coalescingRefineBy,
      eventDescription: eventDescription
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: eventDescription
  });
};
export const getFilterLineForWebinarFilter = filter => {
  const webinarId = filter.webinarId || I18n.text('flow-doctor-lib.filters.webinar.anyWebinar');
  return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.webinar.${filter.operator}`,
    options: {
      webinarId
    }
  });
};
export const FilterLineForSurveyMonkeyFilter = ({
  filter
}) => {
  const {
    data: surveyData,
    loading: loadingSurveyData,
    error: surveyError
  } = useSurveyMonkeySurvey({
    portalId: PortalIdParser.get(),
    surveyId: filter.surveyId
  });
  if (!surveyError && (loadingSurveyData || !surveyData)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  if (filter.filterType === 'SURVEY_MONKEY') {
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `flow-doctor-lib.filters.surveyMonkey.${filter.operator}`,
      options: {
        surveyId: surveyData ? surveyData.survey.title : filter.surveyId
      }
    });
  }
  const matchingQuestion = surveyData && surveyData.questions.find(q => q.questionId === filter.surveyQuestion);
  return /*#__PURE__*/_jsx(FilterLineForPropertyFilter, {
    filter: {
      filterType: 'PROPERTY',
      property: filter.surveyQuestion,
      operation: filter.valueComparison
    },
    propertyLabel: matchingQuestion ? matchingQuestion.questionText : undefined
  });
};
const FilterLineForIntegrationFilter = ({
  filterLine,
  eventId
}) => {
  const {
    data,
    loading,
    error
  } = useLegacyIntegrationEventProperties({
    portalId: PortalIdParser.get(),
    eventId
  });
  if (!error && (loading || !data)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  return /*#__PURE__*/_jsx(FilterLineForPropertyFilter, {
    filter: filterLine,
    propertyLabel: data && data[filterLine.property] ? data[filterLine.property].label : undefined,
    propertyValueLabels: data && data[filterLine.property] ? data[filterLine.property].options.reduce((acc, option) => Object.assign({}, acc, {
      [option.value]: option.label
    }), {}) : undefined
  });
};
export const FilterLinesForIntegrationFilter = ({
  filter
}) => {
  return /*#__PURE__*/_jsx(_Fragment, {
    children: filter.filterLines.map((filterLine, i) => /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FilterLineForIntegrationFilter, {
          filterLine: Object.assign({}, filterLine, {
            filterType: 'PROPERTY'
          }),
          eventId: filter.eventTypeId
        })
      }), i < filter.filterLines.length - 1 && /*#__PURE__*/_jsx(WalmartFilterBranchOperatorLabel, {
        filterBranchOperator: "AND"
      })]
    }, i))
  });
};
export const getFilterLineForAdsTimeFilter = filter => {
  return /*#__PURE__*/_jsx(FilterLineWithPruningRefineBy, {
    pruningRefineBy: filter.pruningRefineBy,
    eventDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "flow-doctor-lib.filters.adsTime.filterLine"
    })
  });
};
export const FilterLineForLegacyEventFilter = ({
  filter
}) => {
  const {
    data,
    loading,
    error
  } = useLegacyCBE({
    portalId: PortalIdParser.get(),
    cbeId: filter.eventId
  });
  if (!error && (loading || !data)) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  const eventDescription = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `flow-doctor-lib.filters.legacyEvent.${filter.operator}`,
    options: {
      eventName: data ? data.name : filter.eventId
    }
  });
  if (filter.pruningRefineBy) {
    return /*#__PURE__*/_jsx(FilterLineWithPruningRefineBy, {
      pruningRefineBy: filter.pruningRefineBy,
      eventDescription: eventDescription
    });
  }
  if (filter.coalescingRefineBy) {
    return /*#__PURE__*/_jsx(FilterLineWithCoalescingRefineBy, {
      coalescingRefineBy: filter.coalescingRefineBy,
      eventDescription: eventDescription
    });
  }
  return eventDescription;
};
export const groupAssociationFiltersByIndexAndObjectType = filterBranch => {
  const groupedAssociationFilters = {};
  filterBranch.filterBranches.forEach((topAndBranch, index) => {
    topAndBranch.filterBranches.forEach(branch => {
      if (branch.filterBranchType === 'ASSOCIATION') {
        const existingObjectTypeFilters = index in groupedAssociationFilters ? groupedAssociationFilters[index] : {};
        groupedAssociationFilters[index] = Object.assign({}, existingObjectTypeFilters, {
          [branch.objectTypeId]: branch.filters
        });
      }
    });
  });
  return groupedAssociationFilters;
};